import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft, TrendingUp } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { blogPosts, getBlogBySlug } from '@/data/blogPosts';

const BlogDetails = () => {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <SEO title="Blog Not Found" description="Requested blog article could not be found." />
        <section className="pt-32 pb-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Article not found</h1>
            <p className="text-gray-600 mb-8">The blog post you are looking for does not exist.</p>
            <Link
              to="/blogs"
              className="inline-flex items-center px-6 py-3 rounded-xl bg-blue-800 text-white hover:bg-blue-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blogs
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const trendingTopics = [...new Set(blogPosts.flatMap((p) => p.tags))].slice(0, 8);
  const blogPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    author: {
      '@type': 'Person',
      name: post.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'Boxd Logistics',
      url: 'https://boxdlogistics.in'
    },
    datePublished: `${post.publishedAt}T00:00:00+05:30`,
    dateModified: `${post.publishedAt}T00:00:00+05:30`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://boxdlogistics.in/blogs/${post.slug}`
    },
    articleSection: post.category,
    keywords: post.tags.join(', ')
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://boxdlogistics.in/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blogs',
        item: 'https://boxdlogistics.in/blogs'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: `https://boxdlogistics.in/blogs/${post.slug}`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={`${post.tags.join(', ')}, ecommerce shipping, logistics insights, courier aggregator, delivery operations`}
        canonical={`https://boxdlogistics.in/blogs/${post.slug}`}
        image={post.image}
        type="article"
        author={post.author}
        article={{
          publishedTime: `${post.publishedAt}T00:00:00+05:30`,
          modifiedTime: `${post.publishedAt}T00:00:00+05:30`,
          section: post.category,
          tags: post.tags
        }}
        structuredData={[blogPostingSchema, breadcrumbSchema]}
        structuredDataId="schema-blog-detail"
      />
      <Navbar />

      <section className="relative pt-32 pb-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        ></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-blue-300/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-gradient-to-br from-blue-100/20 to-blue-200/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-5xl mx-auto">
          <Link to="/blogs" className="inline-flex items-center text-blue-800 hover:text-blue-900 font-medium mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blogs
          </Link>

          <div className={`h-2 rounded-full bg-gradient-to-r ${post.gradient} mb-6`}></div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{post.title}</h1>
          <p className="text-lg text-gray-600 mb-6">{post.excerpt}</p>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="inline-flex items-center text-gray-600">
              <Calendar className="w-4 h-4 mr-1" />
              {post.date}
            </span>
            <span className="inline-flex items-center text-blue-900 font-semibold">
              <Clock className="w-4 h-4 mr-1" />
              {post.readTime}
            </span>
            <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-900 font-semibold">{post.category}</span>
          </div>
        </div>
      </section>

      <section className="py-14 px-4">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
          <article className="lg:col-span-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="mb-8 overflow-hidden rounded-xl border border-gray-100">
              <img
                src={post.image}
                alt={post.imageAlt}
                className="h-[280px] w-full object-cover"
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="space-y-5">
              {post.content.map((paragraph) => (
                <p key={paragraph} className="text-gray-700 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </article>

          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4 inline-flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-blue-800" />
                Trending Topics
              </h2>
              <div className="flex flex-wrap gap-2">
                {trendingTopics.map((topic) => (
                  <span key={topic} className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-900 rounded-full text-sm font-semibold">
                    <Tag className="w-3 h-3 mr-1" />
                    {topic}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-6 shadow-sm">
              <p className="text-sm font-semibold text-blue-900 mb-1">Written by</p>
              <p className="text-lg font-bold text-gray-900">{post.author}</p>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetails;
